/* eslint-disable no-unused-vars */
import React, {
  useCallback,
  useContext,
  useEffect,
  createContext,
  useState,
  useMemo,
  useRef,
} from 'react'

import { SearchEdge, useSearchLazyQuery } from '@/apollo/graphql'
import { cleanList } from '@/utils/index'
import { TModelFilter } from './components/ModelFilter'

interface ISearchContext {
  text: string | undefined
  onChangeText: (v: string) => void
  filter: TModelFilter[]
  onChangeFilter: (f: TModelFilter) => void
  searchRes: SearchEdge[]
  loading: boolean
  clearFilters: () => void
}

const SearchContext = createContext<ISearchContext>({
  text: '',
  onChangeText: () => null,
  filter: [],
  onChangeFilter: () => null,
  searchRes: [],
  loading: false,
  clearFilters: () => null,
})

export const SearchContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const timer = useRef<NodeJS.Timeout>()
  const [searchRes, setSearchRes] = useState<SearchEdge[]>([])
  const [text, setText] = useState<string | undefined>()
  const [filter, setFilter] = useState<TModelFilter[] | undefined>()

  const [search, { loading }] = useSearchLazyQuery({
    fetchPolicy: 'cache-and-network',
    onCompleted: res => {
      setSearchRes(cleanList<SearchEdge>(res?.search?.edges))
    },
  })

  useEffect(() => {
    const debounce = text ? 300 : 0
    if (filter || (text !== null && text !== undefined)) {
      timer.current = setTimeout(() => {
        search({
          variables: {
            text,
            models: filter,
          },
        })
      }, debounce)
    }

    return () => {
      if (timer.current) {
        clearTimeout(timer.current)
      }
    }
  }, [text, search, filter])

  const onChangeFilter = useCallback(
    (f: TModelFilter) =>
      setFilter(prev => {
        if (prev?.includes(f)) {
          return prev.filter(p => p !== f)
        }
        return [...(prev || []), f]
      }),
    [],
  )

  const clearFilters = useCallback(() => {
    setFilter(() => [])
  }, [])

  const value = useMemo(
    () => ({
      text,
      onChangeText: setText,
      filter: filter || [],
      onChangeFilter,
      searchRes,
      loading,
      clearFilters,
    }),
    [filter, loading, onChangeFilter, searchRes, text, clearFilters],
  )

  return (
    <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
  )
}

export const useSearch = () => useContext(SearchContext)
